import type { GovernancePolicyFormData } from '@experiences/interfaces';
import {
    PolicyStatus,
    Scope,
} from '@experiences/interfaces';
import {
    useCallback,
    useMemo,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import useSWR from 'swr';

import {
    useResourceCatalogNameMap,
    useTagLogicText,
} from '../../../../../common/hooks/useResourceCatalogNameMap';
import { getOrgLogicalNameAndTenantName } from '../../../../../services/organization/TenantService';
import { useTenantsContext } from '../../../../tenants/TenantsContextProvider';
import {
    ACCESS_DECISION_KEYS,
    ACTOR_IDENTITY_TRANSLATION_KEYS,
    AUTOMATION_TRANSLATION_KEYS,
    type DisplayItem,
    resolveDisplayItem,
    RESOURCE_TRANSLATION_KEYS,
} from '../../governanceConstants';
import { collectEntityKeysFromSelectorsAndExecutableRule } from '../common/ResourceCatalogUtils';
import { useActorRuleNameResolution } from '../common/useActorRuleNameResolution';

interface ResourcesDisplay {
    items: DisplayItem[];
    tags: string[];
    tagLogicText: string;
}

interface AutomationsDisplay {
    items: DisplayItem[];
    tags: string[];
    tagLogicText: string;
}

interface ActorIdentityDisplay {
    items: DisplayItem[];
}

const useGovernancePolicyPreviewViewModel = () => {
    const { formatMessage: translate } = useIntl();
    const { watch } = useFormContext<GovernancePolicyFormData>();
    const { tenantsWithoutServices } = useTenantsContext();

    const scope = watch('scope');
    const tenantId = watch('tenantId');
    const name = watch('name');
    const description = watch('description');
    const selectors = watch('selectors');
    const executableRule = watch('executableRule');
    const actorRule = watch('actorRule');
    const enforcement = watch('enforcement');
    const status = watch('status');

    // Resolve tenant name from tenantId (needed for Resource Catalog calls)
    const { data: orgTenantInfo } = useSWR(
        tenantId ? tenantId : null,
        getOrgLogicalNameAndTenantName,
    );
    const tenantName = orgTenantInfo?.tenantName;
    const getTagLogicText = useTagLogicText();

    // Resolve actor identity and group names from services (fallback for missing valueNames)
    const {
        actorIdentityNameMap, groupNameMap: resolvedGroupNameMap,
    } = useActorRuleNameResolution(actorRule);

    // Collect all concrete entity keys (excluding wildcard-only "*") used in resources and automations
    const allEntityKeys = useMemo(
        () => collectEntityKeysFromSelectorsAndExecutableRule(selectors, executableRule),
        [ selectors, executableRule ],
    );

    const {
        idToNameMap,
        isLoadingNames,
    } = useResourceCatalogNameMap(
        tenantName,
        allEntityKeys,
        'governancePolicyReviewNamesByKeys',
    );

    const scopeDisplay = useCallback(() => {
        if (scope === Scope.Tenant && tenantId) {
            const tenant = tenantsWithoutServices?.find(t => t.id === tenantId);
            return tenant?.name ?? tenantId;
        }
        return translate({ id: 'CLIENT_GOVERNANCE_SCOPE_ENTIRE_ORGANIZATION' });
    }, [ scope, tenantId, tenantsWithoutServices, translate ]);

    // Format resources display
    const resourcesDisplay = useMemo<ResourcesDisplay | null>(() => {
        const currentSelectors = selectors ?? [];
        if (!currentSelectors || currentSelectors.length === 0) {
            return null;
        }

        const valueNameMap = idToNameMap ?? {};
        const items: DisplayItem[] = currentSelectors
            .map(selector => resolveDisplayItem(selector, selector.resourceType, RESOURCE_TRANSLATION_KEYS, translate, valueNameMap))
            .filter((item): item is DisplayItem => item != null);

        // Get tags from first selector (tags are shared across all selectors)
        const firstSelector = currentSelectors[0];
        const tags = firstSelector?.tags?.values ?? [];
        const tagOperator = firstSelector?.tags?.operator;

        return {
            items,
            tags,
            tagLogicText: getTagLogicText(tagOperator),
        };
    }, [ selectors, translate, idToNameMap, getTagLogicText ]);

    // Format automations display
    const automationsDisplay = useMemo<AutomationsDisplay | null>(() => {
        if (!executableRule?.values || executableRule.values.length === 0) {
            return null;
        }

        const valueNameMap = idToNameMap ?? {};
        const items: DisplayItem[] = executableRule.values
            .map(value => resolveDisplayItem(value, value.type, AUTOMATION_TRANSLATION_KEYS, translate, valueNameMap))
            .filter((item): item is DisplayItem => item != null);

        // Get tags from executableRule
        const tags = executableRule.tags?.values ?? [];
        const tagOperator = executableRule.tags?.operator;

        return {
            items,
            tags,
            tagLogicText: getTagLogicText(tagOperator),
        };
    }, [ executableRule, translate, idToNameMap, getTagLogicText ]);

    // Format actor identity display (includes Groups as ActorType.Group entries in actorRule.values)
    const actorIdentityDisplay = useMemo<ActorIdentityDisplay | null>(() => {
        if (!actorRule?.values || actorRule.values.length === 0) {
            return null;
        }

        const items: DisplayItem[] = actorRule.values
            .map(value => {
                // Merge resolved names with form valueNames (resolved takes precedence as fallback)
                const mergedNameMap = {
                    ...actorIdentityNameMap,
                    ...resolvedGroupNameMap,
                    ...(value.valueNames ?? {}),
                };
                return resolveDisplayItem(value, value.type, ACTOR_IDENTITY_TRANSLATION_KEYS, translate, mergedNameMap);
            })
            .filter((item): item is DisplayItem => item != null);

        return { items };
    }, [ actorRule, translate, actorIdentityNameMap, resolvedGroupNameMap ]);

    // Format access decision display
    const accessDecisionDisplay = useMemo(() => {
        if (!enforcement) {
            return null;
        }
        const translationKey = ACCESS_DECISION_KEYS[enforcement];
        return translationKey ? translate({ id: translationKey }) : null;
    }, [ enforcement, translate ]);

    // Format enforcement mode display + description in one pass
    const enforcementMode = useMemo(() => {
        if (!status || status === PolicyStatus.Deleted) {
            return {
                display: null,
                description: null,
            };
        }

        const statusMap: Record<Exclude<PolicyStatus, PolicyStatus.Deleted>, { title: string; desc: string }> = {
            [PolicyStatus.Simulated]: {
                title: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_SIMULATION_TITLE',
                desc: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_SIMULATION_DESCRIPTION',
            },
            [PolicyStatus.Active]: {
                title: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_ENABLED_TITLE',
                desc: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_ENABLED_DESCRIPTION',
            },
            [PolicyStatus.Disabled]: {
                title: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_DISABLED_TITLE',
                desc: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_DISABLED_DESCRIPTION',
            },
        };

        const entry = statusMap[status];
        return entry
            ? {
                display: translate({ id: entry.title }),
                description: translate({ id: entry.desc }),
            }
            : {
                display: null,
                description: null,
            };
    }, [ status, translate ]);

    return {
        scopeDisplay,
        resourcesDisplay,
        automationsDisplay,
        actorIdentityDisplay,
        accessDecisionDisplay,
        enforcement,
        enforcementModeDisplay: enforcementMode.display,
        enforcementModeDescription: enforcementMode.description,
        name,
        description,
        isLoadingNames,
    };
};

export default useGovernancePolicyPreviewViewModel;
